import { meshUtils } from '@fe/maplibre-extensions';
import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { InterpolationType } from '../../../settings/settings';
import fragmentSource from './image.fragment.glsl?raw';
import vertexSource from './image.vert.glsl?raw';

export class ImageLayer implements CustomLayerInterface {
    readonly type = 'custom';
    readonly renderingMode?: '2d';
    private shaderMap = new Map<string, WebGLProgram>();
    private aPos: GLint = 0; // TODO: use VAO
    private texCoords: GLint = 0; // TODO: use VAO
    private vertexBuffer: WebGLBuffer | null = null;
    private texCoordsBuffer: WebGLBuffer | null = null;
    private indexBuffer: WebGLBuffer | null = null;
    private map: MaplibreMap | null = null;
    private indexCount = 0;
    private texture: WebGLTexture | null = null;
    private texRes: [number, number] = [0, 0];
    private ramp: WebGLTexture | null = null;
    private gl: WebGL2RenderingContext | null = null;
    private drawMesh = false;
    private _opacity = 1.0;
    private _minLevel = 0.0;
    private _smoothing = { enabled: false, linear: false, groupSize: 255 };

    constructor(public readonly id: string) {}

    // TODO: implement picking with frame buffer

    private getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vs = vertexSource
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vs);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        this.aPos = gl.getAttribLocation(program, 'a_pos');
        this.texCoords = gl.getAttribLocation(program, 'tex_coords');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    get opacity(): number {
        return this._opacity;
    }

    set smoothing(value: typeof this._smoothing) {
        this._smoothing = value;
        const gl = this.gl;
        if (gl && this.texture) {
            gl.bindTexture(gl.TEXTURE_2D, this.texture);
            gl.texParameteri(
                gl.TEXTURE_2D,
                gl.TEXTURE_MAG_FILTER,
                this._smoothing.enabled && this._smoothing.linear ? gl.LINEAR : gl.NEAREST
            );
            gl.texParameteri(
                gl.TEXTURE_2D,
                gl.TEXTURE_MIN_FILTER,
                this._smoothing.enabled && this._smoothing.linear ? gl.LINEAR : gl.NEAREST
            );
        }
        this.map?.triggerRepaint();
    }

    set opacity(value: number) {
        this._opacity = value;
        this.map?.triggerRepaint();
    }

    set minLevel(value: number) {
        this._minLevel = value;
        this.map?.triggerRepaint();
    }

    get minLevel() {
        return this._minLevel;
    }

    updateBounds(bounds: [number, number, number, number]) {
        if (!this.gl) {
            return;
        }

        const gl = this.gl;
        const mesh = meshUtils.generateRectangularMesh(bounds, 1);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordsBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mesh.texCoords), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mesh.verticesFlattened), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(mesh.triangleIndices), gl.STATIC_DRAW);

        this.indexCount = mesh.triangleIndices.length;
        this.map?.triggerRepaint();
    }

    updateRamp(ramp: Uint8Array, interpolation: InterpolationType) {
        // Ramp
        if (!this.gl) {
            return;
        }

        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.ramp);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, ramp.byteLength / 4, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, ramp);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, interpolation === 'linear' ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, interpolation === 'linear' ? gl.LINEAR : gl.NEAREST);
        this.map?.triggerRepaint();
    }

    updateImage(image: Uint8Array, width: number, height: number, interpolation: InterpolationType) {
        if (!this.gl) {
            return;
        }
        const gl = this.gl;
        this.texRes = [width, height];
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, image);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(
            gl.TEXTURE_2D,
            gl.TEXTURE_MAG_FILTER,
            (this._smoothing.enabled && this._smoothing.linear) || interpolation === 'linear' ? gl.LINEAR : gl.NEAREST
        );
        gl.texParameteri(
            gl.TEXTURE_2D,
            gl.TEXTURE_MIN_FILTER,
            (this._smoothing.enabled && this._smoothing.linear) || interpolation === 'linear' ? gl.LINEAR : gl.NEAREST
        );
    }
    onAdd(map: MaplibreMap, gl: WebGL2RenderingContext): void {
        this.gl = gl;
        this.map = map;

        this.vertexBuffer = gl.createBuffer();
        this.indexBuffer = gl.createBuffer();
        this.texCoordsBuffer = gl.createBuffer();
        this.texture = gl.createTexture();
        this.ramp = gl.createTexture();
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.gl = null;
        this.map = null;

        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.indexBuffer);
        gl.deleteBuffer(this.texCoordsBuffer);
        gl.deleteTexture(this.texture);
        gl.deleteTexture(this.ramp);

        gl.disableVertexAttribArray(this.aPos);
        gl.disableVertexAttribArray(this.texCoords);

        this.vertexBuffer = null;
        this.indexBuffer = null;
        this.texCoordsBuffer = null;

        for (const program of this.shaderMap.values()) {
            gl.deleteProgram(program);
        }

        this.shaderMap.clear();
    }

    render(gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.map) {
            return;
        }

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.DST_ALPHA);

        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData)!;
            gl.useProgram(program);
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );

            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform1f(gl.getUniformLocation(program, 'u_mesh'), 0.0);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);
            gl.uniform1f(gl.getUniformLocation(program, 'u_min_level'), this._minLevel);
            gl.uniform2f(gl.getUniformLocation(program, 'u_tex_res'), ...this.texRes);
            gl.uniform1f(gl.getUniformLocation(program, 'u_smoothing'), this._smoothing.enabled ? 1.0 : 0.0);
            gl.uniform1f(gl.getUniformLocation(program, 'u_group_size'), this._smoothing.groupSize);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.texture!);
            gl.uniform1i(gl.getUniformLocation(program, 'u_texture'), 0);

            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this.ramp!);
            gl.uniform1i(gl.getUniformLocation(program, 'u_ramp'), 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordsBuffer);
            gl.enableVertexAttribArray(this.texCoords);
            gl.vertexAttribPointer(this.texCoords, 2, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
            gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);

            if (this.drawMesh) {
                gl.uniform1f(gl.getUniformLocation(program, 'u_mesh'), 1.0);
                gl.drawElements(gl.LINE_STRIP, this.indexCount, gl.UNSIGNED_SHORT, 0);
            }
        });
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}
